import type { AriaAttributes, ForwardedRef, FunctionComponentElement, ReactElement } from 'react';
import { Children, cloneElement, useEffect, useRef, useState } from 'react';
import { cx } from 'classix';

import type { PopoverProps } from '@launchpad-ui/popover';
import { Popover } from '@launchpad-ui/popover';

function assignElementRef(elementRef: ForwardedRef<HTMLElement | null> | undefined, node: HTMLElement | null) {
	if (typeof elementRef === 'function') {
		elementRef(node);
		return;
	}
	if (elementRef === null || elementRef === undefined) {
		return;
	}
	const refObject = elementRef;
	// oxlint-disable-next-line no-param-reassign -- forward the instance onto the caller's ref
	refObject.current = node;
}

type DropdownState = {
	isOpen?: boolean;
};

type DropdownProps<T extends string | object | number> = PopoverProps & {
	onSelect?: (item: T, stateChanges: DropdownState) => void;
	onStateChange?: (state: DropdownState) => void;
};

/**
 * @deprecated use `Menu` from `@launchpad-ui/components` instead
 *
 * https://launchpad.launchdarkly.com/?path=/docs/components-collections-menu--docs
 */
const Dropdown = <T extends string | object | number>(props: DropdownProps<T>) => {
	const {
		placement,
		disabled,
		targetClassName,
		popoverClassName,
		isOpen: isOpenProp,
		onInteraction,
		onSelect,
		onStateChange,
		children,
		'data-test-id': testId = 'dropdown',
		...rest
	} = props;

	const [triggerNode, setTriggerNode] = useState<HTMLElement | null>(null);
	const [isOpen, setIsOpen] = useState(isOpenProp ?? false);
	const [openProp, setOpenProp] = useState(isOpenProp);
	if (openProp !== isOpenProp) {
		setOpenProp(isOpenProp);
		if (isOpenProp !== undefined) {
			setIsOpen(isOpenProp);
		}
	}

	const onStateChangeRef = useRef(onStateChange);
	useEffect(() => {
		onStateChangeRef.current = onStateChange;
	});

	useEffect(() => {
		onStateChangeRef.current?.({ isOpen });
	}, [isOpen]);

	const wasOpenRef = useRef(isOpen);
	useEffect(() => {
		const wasOpen = wasOpenRef.current;
		wasOpenRef.current = isOpen;
		if (!wasOpen || isOpen) {
			return;
		}
		const timeoutId = window.setTimeout(() => {
			const current = triggerNode;
			if (!current) {
				return;
			}
			const hasModal = current.closest?.('.has-overlay');
			if (!hasModal) {
				current.focus?.();
			}
		});
		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [isOpen, triggerNode]);

	const handleSelect = (item: T) => {
		setIsOpen(false);
		onSelect?.(item, { isOpen: false });
	};

	const handlePopoverInteraction = (nextIsOpen: boolean) => {
		setIsOpen(nextIsOpen);
	};

	const [targetChild, contentChild] = Children.toArray(children);
	const target = targetChild as FunctionComponentElement<AriaAttributes & { isopen: string }> & {
		props: { ref?: ForwardedRef<HTMLElement | null> };
	};
	const content = contentChild as ReactElement<{ onSelect?: (item: T) => void }>;
	const forwardedRef = target.props.ref;
	const assignTrigger = (node: HTMLElement | null) => {
		assignElementRef(forwardedRef, node);
		setTriggerNode(node);
	};

	const trigger = cloneElement(target, {
		'aria-haspopup': true,
		'aria-expanded': !!isOpen,
		ref: assignTrigger,
		isopen: isOpen?.toString(),
	});
	const menuContent = cloneElement(content, {
		onSelect: handleSelect,
	});

	const popoverTargetClasses = cx('Dropdown-target', targetClassName);
	const popoverClasses = cx('Dropdown', popoverClassName);

	return (
		<Popover
			isOpen={isOpen}
			placement={placement}
			onInteraction={onInteraction || handlePopoverInteraction}
			restrictHeight={false}
			disabled={disabled}
			targetClassName={popoverTargetClasses}
			popoverClassName={popoverClasses}
			data-test-id={testId}
			{...rest}
		>
			{trigger}
			{menuContent}
		</Popover>
	);
};

export { Dropdown };
export type { DropdownProps };
